import { type Dispatch, type SetStateAction } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useErrorContext } from '@wf/sw-ui-kit';
import { Button } from '@wf/components';

import { useLicenseContext } from '../../../LicenseModeContext';
import Separator from '../../../../../components/Separator';

import { ArrayFields, AttachedDocumentsTabFields } from '../../../types/fieldEnums';
import { type FormType } from '../types';

import { attachmentPermissionsSelector } from '../../../store/selectors';

interface IFormHeaderProps {
  index: number;
  type: FormType;
  viewMode: boolean;
  setViewMode: Dispatch<SetStateAction<boolean>>;
  onDelete: () => void;
  onCancel: () => void;
  onSave: () => void;
}

function FormHeader({ index, type, viewMode, setViewMode, onDelete, onSave, onCancel }: IFormHeaderProps) {
  const { t } = useTranslation();

  const { control } = useFormContext();
  const { triggerWithBackendErrors } = useErrorContext();

  const permissions = useSelector(attachmentPermissionsSelector);
  const { viewMode: mainViewMode } = useLicenseContext();

  const id = useWatch({
    name: `${ArrayFields.attachedDocuments}.${index}.${AttachedDocumentsTabFields.id}`,
    control,
  });

  const handleSave = async () => {
    const isValid = await triggerWithBackendErrors(`${ArrayFields.attachedDocuments}.${index}`);

    if (isValid) {
      onSave();
    }
  };

  const showEditButton = !mainViewMode && (!!permissions?.modify || !id);
  const showDeleteButton = !mainViewMode && (!!permissions?.delete || !id);

  return (
    <>
      <div className="flex space-between">
        <span className="fs-20 fw-semibold  typography">
          {t(type === 'create' ? 'New Document' : 'Document Description')}
        </span>
        <div className="flex gap-12">
          {viewMode ? (
            <>
              {showDeleteButton && (
                <Button onClick={onDelete} color="typography" type="button" size="sm" secondary>
                  {t('Delete')}
                </Button>
              )}
              {showEditButton && (
                <Button onClick={() => setViewMode(false)} size="sm" type="button">
                  {t('Edit')}
                </Button>
              )}
            </>
          ) : (
            <>
              <Button onClick={onCancel} color="typography" type="button" size="sm" ghost>
                {t('Cancel')}
              </Button>
              <Button type="button" size="sm" onClick={handleSave}>
                {type === 'edit' ? t('Save Changes') : t('Add To List')}
              </Button>
            </>
          )}
        </div>
      </div>
      <Separator className="m-y-16" />
    </>
  );
}

export default FormHeader;
